import React, { useState, useEffect, useCallback } from 'react';
import './Farm.css';

export type SeedType = 'potato' | 'cucumber' | 'strawberry' | 'clown' | 'alien' | 'gun';

export interface SeedConfig {
  name: string;
  emoji: string;
  price: number;
  growthTime: number; // in seconds
  sellPrice: number;
}

export const SEED_TYPES: Record<SeedType, SeedConfig> = {
  potato: {
    name: 'Potato',
    emoji: '🥔',
    price: 10,
    growthTime: 30,
    sellPrice: 25,
  },
  cucumber: {
    name: 'Cucumber',
    emoji: '🥒',
    price: 20,
    growthTime: 45,
    sellPrice: 50,
  },
  strawberry: {
    name: 'Strawberry',
    emoji: '🍓',
    price: 50,
    growthTime: 60,
    sellPrice: 150,
  },
  clown: {
    name: 'Clown',
    emoji: '🤡',
    price: 100,
    growthTime: 90,
    sellPrice: 400,
  },
  alien: {
    name: 'Alien',
    emoji: '👽',
    price: 50000,
    growthTime: 600, // 10 minutes
    sellPrice: 200000,
  },
  gun: {
    name: 'Gun',
    emoji: '🔫',
    price: 500000,
    growthTime: 3600, // 1 hour
    sellPrice: 5000000,
  },
};

interface Plot {
  id: number;
  unlocked: boolean;
  seedType: SeedType | null;
  plantedAt: number | null; // timestamp
  isReady: boolean;
}

interface FarmProps {
  isOpen: boolean;
  onClose: () => void;
  coins: number;
  onCoinsChange: (delta: number) => void;
}

const TOTAL_PLOTS = 36;
const INITIAL_UNLOCKED = 9;
const PLOT_UNLOCK_COST = 1000;

const Farm: React.FC<FarmProps> = ({ isOpen, onClose, coins, onCoinsChange }) => {
  const [plots, setPlots] = useState<Plot[]>(() => {
    const saved = localStorage.getItem('boulderGame_farmPlots');
    if (saved) {
      const loadedPlots = JSON.parse(saved);
      // Recalculate isReady state based on current time and plantedAt timestamp
      const now = Date.now();
      return loadedPlots.map((plot: Plot) => {
        if (plot.seedType && plot.plantedAt && !plot.isReady) {
          const seedConfig = SEED_TYPES[plot.seedType];
          const elapsed = (now - plot.plantedAt) / 1000; // seconds
          if (elapsed >= seedConfig.growthTime) {
            return { ...plot, isReady: true };
          }
        }
        return plot;
      });
    }
    return Array.from({ length: TOTAL_PLOTS }, (_, i) => ({
      id: i,
      unlocked: i < INITIAL_UNLOCKED,
      seedType: null,
      plantedAt: null,
      isReady: false,
    }));
  });

  const [seedInventory, setSeedInventory] = useState<Record<SeedType, number>>(() => {
    const saved = localStorage.getItem('boulderGame_farmSeedInventory');
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      potato: 0,
      cucumber: 0,
      strawberry: 0,
      clown: 0,
      alien: 0,
      gun: 0,
    };
  });

  const [selectedSeed, setSelectedSeed] = useState<SeedType | null>(null);
  const [selectedPlot, setSelectedPlot] = useState<number | null>(null);

  // Update plot readiness based on growth time
  useEffect(() => {
    const interval = setInterval(() => {
      setPlots(prevPlots => {
        const now = Date.now();
        return prevPlots.map(plot => {
          if (plot.seedType && plot.plantedAt && !plot.isReady) {
            const seedConfig = SEED_TYPES[plot.seedType];
            const elapsed = (now - plot.plantedAt) / 1000; // seconds
            if (elapsed >= seedConfig.growthTime) {
              return { ...plot, isReady: true };
            }
          }
          return plot;
        });
      });
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, []);

  // Save plots to localStorage
  useEffect(() => {
    localStorage.setItem('boulderGame_farmPlots', JSON.stringify(plots));
  }, [plots]);

  // Save seed inventory to localStorage
  useEffect(() => {
    localStorage.setItem('boulderGame_farmSeedInventory', JSON.stringify(seedInventory));
  }, [seedInventory]);

  const handleUnlockPlot = useCallback((plotId: number) => {
    if (coins < PLOT_UNLOCK_COST) return;
    
    setPlots(prevPlots => {
      const updated = [...prevPlots];
      if (!updated[plotId].unlocked) {
        updated[plotId] = { ...updated[plotId], unlocked: true };
        onCoinsChange(-PLOT_UNLOCK_COST);
      }
      return updated;
    });
  }, [coins, onCoinsChange]);

  const handleBuySeed = useCallback((seedType: SeedType, quantity: number = 1) => {
    const seedConfig = SEED_TYPES[seedType];
    const totalCost = seedConfig.price * quantity;
    if (coins < totalCost) return;
    
    onCoinsChange(-totalCost);
    setSeedInventory(prev => ({
      ...prev,
      [seedType]: (prev[seedType] || 0) + quantity,
    }));
  }, [coins, onCoinsChange]);

  const handlePlantSeed = useCallback((plotId: number) => {
    if (!selectedSeed) return;
    
    const plot = plots[plotId];
    if (!plot.unlocked || plot.seedType !== null) return;
    if (seedInventory[selectedSeed] <= 0) return;

    setPlots(prevPlots => {
      const updated = [...prevPlots];
      updated[plotId] = {
        ...updated[plotId],
        seedType: selectedSeed,
        plantedAt: Date.now(),
        isReady: false,
      };
      return updated;
    });

    setSeedInventory(prev => {
      const newInventory = {
        ...prev,
        [selectedSeed]: prev[selectedSeed] - 1,
      };
      // Only clear selection if we run out of this seed type
      if (newInventory[selectedSeed] <= 0) {
        setSelectedSeed(null);
      }
      return newInventory;
    });

    setSelectedPlot(null);
  }, [selectedSeed, plots, seedInventory]);

  const handleHarvest = useCallback((plotId: number) => {
    const plot = plots[plotId];
    if (!plot.isReady || !plot.seedType) return;

    const seedConfig = SEED_TYPES[plot.seedType];
    onCoinsChange(seedConfig.sellPrice);

    setPlots(prevPlots => {
      const updated = [...prevPlots];
      updated[plotId] = {
        ...updated[plotId],
        seedType: null,
        plantedAt: null,
        isReady: false,
      };
      return updated;
    });
  }, [plots, onCoinsChange]);

  const formatGrowthTime = (seconds: number): string => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${hours}h`;
    } else if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      if (secs > 0) {
        return `${minutes}m ${secs}s`;
      }
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  const getPlotProgress = (plot: Plot): number => {
    if (!plot.seedType || !plot.plantedAt || plot.isReady) return 0;
    const seedConfig = SEED_TYPES[plot.seedType];
    const elapsed = (Date.now() - plot.plantedAt) / 1000;
    return Math.min(100, (elapsed / seedConfig.growthTime) * 100);
  };

  const getTimeRemaining = (plot: Plot): string => {
    if (!plot.seedType || !plot.plantedAt || plot.isReady) return '';
    const seedConfig = SEED_TYPES[plot.seedType];
    const elapsed = (Date.now() - plot.plantedAt) / 1000;
    const remaining = Math.max(0, seedConfig.growthTime - elapsed);
    
    if (remaining >= 3600) {
      // Show hours and minutes for long times
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      return `${hours}h ${minutes}m`;
    } else if (remaining >= 60) {
      // Show minutes and seconds
      const minutes = Math.floor(remaining / 60);
      const seconds = Math.floor(remaining % 60);
      return `${minutes}m ${seconds}s`;
    } else {
      // Show just seconds
      return `${Math.floor(remaining)}s`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="farm-overlay" onClick={onClose}>
      <div className="farm-popup" onClick={(e) => e.stopPropagation()}>
        <button className="farm-close-button" onClick={onClose}>×</button>
        
        <div className="farm-header">
          <h2>🌾 Farm</h2>
          <div className="farm-coins">Coins: {coins.toLocaleString()} 🪙</div>
        </div>

        <div className="farm-content">
          <div className="farm-seeds-section">
            <h3>Buy Seeds</h3>
            <div className="seeds-grid">
              {Object.entries(SEED_TYPES).map(([type, config]) => {
                const seedType = type as SeedType;
                const inventoryCount = seedInventory[seedType] || 0;
                const maxAffordable = Math.floor(coins / config.price);
                return (
                  <div
                    key={type}
                    className={`seed-card ${selectedSeed === type ? 'selected' : ''} ${coins < config.price ? 'unaffordable' : ''}`}
                  >
                    <div className="seed-emoji">{config.emoji}</div>
                    <div className="seed-name">{config.name}</div>
                    {inventoryCount > 0 && (
                      <div className="seed-inventory">Owned: {inventoryCount}</div>
                    )}
                    <div className="seed-price">Price: {config.price.toLocaleString()} 🪙 each</div>
                    <div className="seed-sell-price">Sell: {config.sellPrice.toLocaleString()} 🪙</div>
                    <div className="seed-growth-time">⏱️ {formatGrowthTime(config.growthTime)}</div>
                    <div className="seed-actions">
                      <button
                        className="seed-action-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (inventoryCount > 0) {
                            setSelectedSeed(seedType);
                            setSelectedPlot(null);
                          }
                        }}
                        disabled={inventoryCount === 0}
                      >
                        {inventoryCount > 0 ? 'Select' : 'No seeds'}
                      </button>
                      <div className="seed-buy-controls">
                        <button
                          className="seed-buy-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBuySeed(seedType, 1);
                          }}
                          disabled={coins < config.price}
                        >
                          Buy 1
                        </button>
                        {maxAffordable >= 10 && (
                          <button
                            className="seed-buy-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBuySeed(seedType, 10);
                            }}
                            disabled={coins < config.price * 10}
                          >
                            Buy 10
                          </button>
                        )}
                        {maxAffordable >= 100 && (
                          <button
                            className="seed-buy-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBuySeed(seedType, 100);
                            }}
                            disabled={coins < config.price * 100}
                          >
                            Buy 100
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedSeed && (
              <div className="selected-seed-info">
                Selected: {SEED_TYPES[selectedSeed].emoji} {SEED_TYPES[selectedSeed].name}
                <br />
                You have {seedInventory[selectedSeed] || 0} in inventory
                <br />
                Click on an unlocked empty plot to plant!
              </div>
            )}
          </div>

          <div className="farm-plots-section">
            <h3>Farm Plots</h3>
            <div className="plots-grid">
              {plots.map((plot) => (
                <div
                  key={plot.id}
                  className={`plot ${!plot.unlocked ? 'locked' : ''} ${plot.isReady ? 'ready' : ''} ${selectedPlot === plot.id ? 'selected' : ''}`}
                  onClick={() => {
                    if (!plot.unlocked) {
                      handleUnlockPlot(plot.id);
                    } else if (selectedSeed && !plot.seedType) {
                      handlePlantSeed(plot.id);
                    } else if (plot.isReady) {
                      handleHarvest(plot.id);
                    } else {
                      setSelectedPlot(plot.id);
                    }
                  }}
                >
                  {!plot.unlocked ? (
                    <div className="plot-locked">
                      <div className="lock-icon">🔒</div>
                      <div className="unlock-cost">{PLOT_UNLOCK_COST} 🪙</div>
                    </div>
                  ) : plot.seedType ? (
                    <div className="plot-planted">
                      <div className="plant-emoji">{SEED_TYPES[plot.seedType].emoji}</div>
                      {plot.isReady ? (
                        <div className="harvest-indicator">✨ Ready!</div>
                      ) : (
                        <>
                          <div className="growth-progress">
                            <div 
                              className="growth-bar" 
                              style={{ width: `${getPlotProgress(plot)}%` }}
                            />
                          </div>
                          <div className="time-remaining">{getTimeRemaining(plot)}</div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="plot-empty">+</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Farm;

